import { useCallback, useEffect, useRef } from 'react'
import { AudioEngine, AudioEngineError } from '../audio/engine'
import {
  isMediaDevicesAvailable,
  isSecureContextAvailable,
  listAudioInputs,
  resolveDeviceId,
} from '../audio/devices'
import { messageFor } from '../audio/errors'
import { liveReadings } from '../state/live-readings'
import { freqOf } from '../music/pitch'
import { useTunerStore, type EngineStatus } from '../state/tuner-store'

/** Controls handed to the UI. */
export interface TunerEngineControls {
  /** Open the microphone. Call directly from a click handler. */
  readonly start: () => void
  /** Release the microphone. */
  readonly stop: () => void
  /** Switch inputs, restarting the pipeline when it is already running. */
  readonly selectDevice: (deviceId: string) => void
  /** Re-read the input list, e.g. when the user opens the picker. */
  readonly refreshDevices: () => void
  /** Pick the audio back up after an interruption. Call it from a tap. */
  readonly resume: () => void
}

/** True while the microphone is ours, interruption or not. */
const isRunningStatus = (status: EngineStatus): boolean =>
  status === 'listening' || status === 'interrupted'

/**
 * Owns the AudioEngine lifetime and pipes its callbacks into the store.
 *
 * `start` deliberately stays synchronous up to the engine call so the browser
 * still sees an active user gesture when the AudioContext is constructed.
 */
export function useTunerEngine(): TunerEngineControls {
  const engineRef = useRef<AudioEngine | null>(null)
  const tuning = useTunerStore((state) => state.tuning)
  const a4 = useTunerStore((state) => state.a4)
  const setStatus = useTunerStore((state) => state.setStatus)
  const setError = useTunerStore((state) => state.setError)
  const setDevices = useTunerStore((state) => state.setDevices)
  const setInterrupted = useTunerStore((state) => state.setInterrupted)
  const selectDeviceInStore = useTunerStore((state) => state.selectDevice)
  const applyTiming = useTunerStore((state) => state.applyTiming)
  const setSampleRate = useTunerStore((state) => state.setSampleRate)
  const reset = useTunerStore((state) => state.reset)

  const getEngine = useCallback((): AudioEngine => {
    engineRef.current ??= new AudioEngine()
    return engineRef.current
  }, [])

  const reportError = useCallback(
    (error: unknown) => {
      if (error instanceof AudioEngineError) {
        setError({ kind: error.kind, message: error.message })
        return
      }
      console.error('[multitimbral] unexpected audio failure', error)
      setError({ kind: 'unknown', message: messageFor('unknown') })
    },
    [setError],
  )

  /** Refresh the device list; labels stay blank until permission is granted. */
  const refreshDevices = useCallback(() => {
    if (!isMediaDevicesAvailable()) return
    listAudioInputs()
      .then((devices) => {
        setDevices(devices)
      })
      .catch((error: unknown) => {
        console.error('[multitimbral] could not list audio inputs', error)
      })
  }, [setDevices])

  const start = useCallback(() => {
    if (!isSecureContextAvailable()) {
      setError({ kind: 'insecure-context', message: messageFor('insecure-context') })
      return
    }
    if (!isMediaDevicesAvailable()) {
      setError({ kind: 'unsupported', message: messageFor('unsupported') })
      return
    }

    const { devices, selectedDeviceId } = useTunerStore.getState()
    const deviceId = resolveDeviceId(devices, selectedDeviceId)

    setStatus('starting')
    getEngine()
      .start(deviceId, {
        // Measurements go straight into the live channel, never into React
        // state: they arrive far faster than anything needs to re-render.
        onLevel: (level) => {
          liveReadings.applyLevel(level)
        },
        onReadings: (readings) => {
          liveReadings.applyReadings(readings, performance.now())
        },
        onTiming: (timing) => {
          applyTiming(timing)
          console.info(
            `[multitimbral] worklet: ~${timing.averageMs.toFixed(5)} ms per ` +
              `${String(timing.blockSize)}-sample block ` +
              `(budget ${timing.budgetMs.toFixed(3)} ms, load ${timing.loadPercent.toFixed(3)} %, ` +
              `${String(timing.slowBlocks)} of ${String(timing.blocks)} blocks reached the ` +
              `${String(timing.clockResolutionMs)} ms clock tick)`,
          )
        },
        onInterruption: setInterrupted,
        onError: reportError,
      })
      .then((result) => {
        setDevices(result.devices)
        setSampleRate(result.sampleRate)
        if (result.deviceId !== null) {
          selectDeviceInStore(result.deviceId)
        }
        setStatus('listening')
      })
      .catch(reportError)
  }, [
    applyTiming,
    getEngine,
    reportError,
    selectDeviceInStore,
    setDevices,
    setError,
    setInterrupted,
    setSampleRate,
    setStatus,
  ])

  /**
   * Pick the audio back up after an interruption.
   *
   * Wired to a tap rather than run automatically: after a call or Siri, iOS
   * only honours a resume that a user gesture asked for.
   */
  const resume = useCallback(() => {
    void getEngine()
      .resume()
      .catch((error: unknown) => {
        console.error('[multitimbral] resume failed', error)
      })
  }, [getEngine])

  const stop = useCallback(() => {
    void getEngine()
      .stop()
      .then(() => {
        reset()
      })
      .catch((error: unknown) => {
        console.error('[multitimbral] stop failed', error)
      })
  }, [getEngine, reset])

  /** Switching inputs mid-session tears the pipeline down and reopens it. */
  const selectDevice = useCallback(
    (deviceId: string) => {
      const wasRunning = isRunningStatus(useTunerStore.getState().status)
      selectDeviceInStore(deviceId)
      if (!wasRunning) return

      setStatus('starting')
      void getEngine()
        .stop()
        .then(() => {
          start()
        })
        .catch(reportError)
    },
    [getEngine, reportError, selectDeviceInStore, setStatus, start],
  )

  // The probe bank is built around the targets, so it has to follow the
  // tuning and the concert pitch. Cheap when nothing moved: the engine
  // compares before it rebuilds.
  useEffect(() => {
    getEngine().setTargets(tuning.strings.map((string) => freqOf(string.midi, a4)))
  }, [a4, getEngine, tuning])

  // Initial list, plus updates when hardware is plugged in or removed.
  useEffect(() => {
    refreshDevices()
    if (!isMediaDevicesAvailable()) return undefined

    const { mediaDevices } = navigator
    mediaDevices.addEventListener('devicechange', refreshDevices)
    return () => {
      mediaDevices.removeEventListener('devicechange', refreshDevices)
    }
  }, [refreshDevices])

  // Granting microphone access reveals the device names, but fires no
  // `devicechange`. Without this the picker keeps showing the anonymous
  // placeholder even though the real list is already available.
  useEffect(() => {
    const permissions = navigator.permissions as Permissions | undefined
    if (typeof permissions?.query !== 'function') return undefined

    let status: PermissionStatus | null = null
    const onChange = (): void => {
      refreshDevices()
    }

    permissions
      // Safari rejects this permission name outright, hence the catch below.
      .query({ name: 'microphone' })
      .then((result) => {
        status = result
        result.addEventListener('change', onChange)
      })
      .catch(() => {
        // Browser does not expose microphone permission state; the picker
        // still refreshes on open and after a successful start.
      })

    return () => {
      status?.removeEventListener('change', onChange)
    }
  }, [refreshDevices])

  useEffect(() => {
    return () => {
      void engineRef.current?.stop()
    }
  }, [])

  return { start, stop, selectDevice, refreshDevices, resume }
}
